import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import {
  agroforestryApi,
  type ApprovalStatus,
  type ApproveAgroforestryReceiptDto,
  type CreateAgroforestryReceiptDto,
} from "../services/agroforestryApi";

const QK = {
  stock: ["agroforestry-stock"] as const,
  pending: ["agroforestry-receipts-pending"] as const,
  receipts: (status?: ApprovalStatus) => ["agroforestry-receipts", status ?? "all"] as const,
};

// Invalidate every cache that an agroforestry mutation could affect — including
// the existing walk-in POS catalogue so newly-ready batches surface immediately.
const invalidateAll = () => {
  queryClient.invalidateQueries({ queryKey: QK.stock });
  queryClient.invalidateQueries({ queryKey: QK.pending });
  queryClient.invalidateQueries({ queryKey: ["agroforestry-receipts"] });
  queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-available"] });
  queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-entries"] });
};

export const useAgroforestryStock = () =>
  useQuery({
    queryKey: QK.stock,
    queryFn: () => agroforestryApi.getStock(),
  });

export const useAgroforestryStockPaged = (params: { pageNumber?: number; pageSize?: number; search?: string; status?: string }) =>
  useQuery({
    queryKey: [...QK.stock, "paged", params],
    queryFn: () => agroforestryApi.getStockPaged(params),
    placeholderData: (prev) => prev,
  });

export const usePendingAgroforestryReceipts = () =>
  useQuery({
    queryKey: QK.pending,
    queryFn: () => agroforestryApi.getPendingReceipts(),
  });

export const useAgroforestryReceipts = (status?: ApprovalStatus) =>
  useQuery({
    queryKey: QK.receipts(status),
    queryFn: () => agroforestryApi.getReceipts(status),
  });

export const useCreateAgroforestryReceipt = () =>
  useMutation({
    mutationFn: (dto: CreateAgroforestryReceiptDto) => agroforestryApi.createReceipt(dto),
    onSuccess: invalidateAll,
  });

export const useApproveAgroforestryReceipt = () =>
  useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: ApproveAgroforestryReceiptDto }) =>
      agroforestryApi.approveReceipt(id, dto),
    onSuccess: invalidateAll,
  });

export const useMarkReadyForSale = () =>
  useMutation({
    mutationFn: ({ entryId, quantity }: { entryId: string; quantity: number }) =>
      agroforestryApi.markReadyForSale(entryId, quantity),
    onSuccess: invalidateAll,
  });
